import { createScopedErrors, registerCodes } from '@norkron/web-errors';
import { forwardRef, Fragment, useRef, useState, useMemo, useEffect, useCallback, useId } from 'react';
import { ErrorBoundary, DefaultErrorFallback } from '@norkron/web-error-boundary';
import { useThemeVar } from '@norkron/web-themer/react';
import { useFocusRestore } from '@norkron/web-a11y';
import { withConformance, registerResponsiveContract } from '@norkron/web-conformance';
import { z } from 'zod';
import { jsx, jsxs } from 'react/jsx-runtime';
import { ErrorState, LoadingState, EmptyState } from '@norkron/web-states';
import { useVirtual } from '@norkron/web-virtual';
import { RichTooltip } from '@norkron/web-tooltip';
import { mru } from '@norkron/web-storage';
import { registerLazy } from '@norkron/web-conformance/register-lazy';

// src/error-codes.ts
var WEB_SEARCH_LIB = "@norkron/web-search";
var WEB_SEARCH_TOKEN = /* @__PURE__ */ Symbol.for("norkron.lib.web-search");
var { warn, panic, reportInvalidInput } = createScopedErrors(WEB_SEARCH_LIB, WEB_SEARCH_TOKEN);
try {
  registerCodes(WEB_SEARCH_LIB, {
    WEB_SEARCH_INVALID_INPUT: {
      description: "Runtime input validation rejected the value passed to a public web-search export (Wave 10 zod mandate). Payload follows WEB_INVALID_INPUT_SHAPE: { fieldPath, expected, received }.",
      severity: "warning",
      docsAnchor: "web-search/invalid-input",
      exampleFix: "Inspect payload.fieldPath + payload.expected; pass input matching the schema exported by this lib."
    },
    SCH_EMPTY_SEARCH_NO_RESULTS: {
      description: 'selfCheck observed an empty search input but a "No results" panel is rendered \u2014 likely a UX dead end on initial mount before the first query is entered.',
      severity: "info",
      docsAnchor: "web-search/self-check#empty-search-no-results",
      exampleFix: "Gate the no-results panel on `query.length > 0` (or show recent searches / suggestions when empty)."
    },
    SCH_SELFCHECK_INTERNAL_ERROR: {
      description: "selfCheck threw internally while inspecting a web-search instance. Exception caught + reported as a finding.",
      severity: "info",
      docsAnchor: "web-search/self-check#internal-error",
      exampleFix: "Inspect finding.message + finding.extras for the underlying error."
    }
  });
} catch {
}

// src/zod-bridge.ts
function toZodLikeSafeParseError(failure) {
  return failure;
}
var DocumentSchema = z.object({
  id: z.string().min(1),
  title: z.string(),
  body: z.string().optional(),
  meta: z.record(z.string(), z.unknown()).optional()
});

// src/__schemas__/Manifest.schema.ts
var ManifestSchema = z.object({
  project: z.string().optional(),
  documents: z.array(DocumentSchema).readonly()
});
var PostingSchema = z.object({
  documentId: z.string().min(1),
  positions: z.array(z.number().int().nonnegative())
});
var SearchIndexSchema = z.object({
  documents: z.record(z.string(), DocumentSchema),
  invertedIndex: z.record(z.string(), z.array(PostingSchema)),
  documentLengths: z.record(z.string(), z.number().int().nonnegative()),
  terms: z.array(z.string())
});

// src/__schemas__/Search.schema.ts
var fn = z.custom((v) => typeof v === "function");
var SearchFilterSchema = z.object({
  id: z.string().min(1),
  label: z.string(),
  value: z.unknown().optional(),
  onRemove: fn
});
var SearchFacetOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
  count: z.number().int().nonnegative().optional()
});
var SearchFacetSchema = z.object({
  id: z.string().min(1),
  label: z.string(),
  options: z.array(SearchFacetOptionSchema).readonly(),
  collapsed: z.boolean().optional()
});
var SearchPropsSchema = z.object({
  manifests: z.array(ManifestSchema).readonly().optional(),
  index: SearchIndexSchema.optional(),
  placeholder: z.string().optional(),
  ariaLabel: z.string().min(1).optional(),
  debounceMs: z.number().int().nonnegative().optional(),
  limit: z.number().int().positive().optional(),
  fuzzy: z.boolean().optional(),
  onSelect: fn.optional(),
  initialQuery: z.string().optional(),
  renderItem: fn.optional(),
  filters: z.array(SearchFilterSchema).readonly().optional(),
  facets: z.array(SearchFacetSchema).readonly().optional(),
  selectedFacets: z.record(z.string(), z.array(z.string()).readonly()).optional(),
  onFacetToggle: fn.optional(),
  recentsKey: z.string().min(1).optional(),
  recentsLimit: z.number().int().positive().optional(),
  loading: z.boolean().optional(),
  error: z.union([z.string(), z.instanceof(Error), z.null()]).optional(),
  onRetry: fn.optional(),
  className: z.string().optional(),
  style: z.record(z.string(), z.unknown()).optional(),
  testId: z.string().optional()
});
var SearchSchema = SearchPropsSchema;
var SearchHitComponentPropsSchema = z.object({
  text: z.string(),
  query: z.string(),
  className: z.string().optional(),
  caseSensitive: z.boolean().optional()
});
var fn2 = z.custom((v) => typeof v === "function");
var SearchInputPropsSchema = z.object({
  value: z.string(),
  onChange: fn2,
  placeholder: z.string().optional(),
  ariaLabel: z.string().min(1).optional(),
  listboxId: z.string().min(1),
  activeId: z.string().nullable().optional(),
  onKeyDown: fn2.optional(),
  onFocus: fn2.optional(),
  onBlur: fn2.optional(),
  autoFocus: z.boolean().optional(),
  className: z.string().optional(),
  testId: z.string().optional()
});
var SearchInputSchema = SearchInputPropsSchema;
var SearchSegmentSchema = z.object({
  text: z.string(),
  match: z.boolean()
});
var SearchSnippetSchema = z.object({
  segments: z.array(SearchSegmentSchema)
});
var SearchHitSchema = z.object({
  document: DocumentSchema,
  score: z.number(),
  matchedTerms: z.array(z.string()),
  snippets: z.array(SearchSnippetSchema)
});

// src/__schemas__/SearchResults.schema.ts
var fn3 = z.custom((v) => typeof v === "function");
var SearchResultsPropsSchema = z.object({
  hits: z.array(SearchHitSchema),
  query: z.string(),
  activeIndex: z.number().int(),
  listboxId: z.string().min(1),
  onSelect: fn3,
  onHover: fn3.optional(),
  renderItem: fn3.optional(),
  loading: z.boolean().optional(),
  error: z.union([z.string(), z.instanceof(Error), z.null()]).optional(),
  onRetry: fn3.optional(),
  testId: z.string().optional()
});
var SearchResultsSchema = SearchResultsPropsSchema;
var fn4 = z.custom((v) => typeof v === "function");
var SearchResultItemPropsSchema = z.object({
  hit: SearchHitSchema,
  query: z.string(),
  active: z.boolean(),
  id: z.string().min(1),
  onSelect: fn4,
  onMouseEnter: fn4.optional(),
  testId: z.string().optional()
});
var SearchResultItemSchema = SearchResultItemPropsSchema;
var UseSearchOptionsSchema = z.object({
  manifests: z.array(ManifestSchema).readonly().optional(),
  index: SearchIndexSchema.optional(),
  debounceMs: z.number().int().nonnegative().optional(),
  limit: z.number().int().positive().optional(),
  fuzzy: z.boolean().optional(),
  initialQuery: z.string().optional()
});
var indexManifestsInputSchema = z.object({
  manifests: z.array(ManifestSchema).readonly()
});
var indexManifestsSchema = indexManifestsInputSchema;
var SearchManifestsOptionsSchema = z.object({
  limit: z.number().int().positive().optional(),
  fuzzy: z.boolean().optional(),
  maxSnippets: z.number().int().nonnegative().optional(),
  snippetLength: z.number().int().positive().optional()
});

// src/__schemas__/searchManifests.schema.ts
var searchManifestsInputSchema = z.object({
  index: SearchIndexSchema,
  query: z.string(),
  options: SearchManifestsOptionsSchema.optional()
});
var searchManifestsSchema = searchManifestsInputSchema;
var HighlightSegmentsOptionsSchema = z.object({
  caseSensitive: z.boolean().optional()
});
var highlightSegmentsInputSchema = z.object({
  text: z.string(),
  query: z.string(),
  options: HighlightSegmentsOptionsSchema.optional()
});
var highlightSegmentsSchema = highlightSegmentsInputSchema;
var highlightSegmentsForTermsInputSchema = z.object({
  text: z.string(),
  terms: z.array(z.string()),
  options: HighlightSegmentsOptionsSchema.optional()
});
var highlightSegmentsForTermsSchema = highlightSegmentsForTermsInputSchema;
var SearchStatusSchema = z.union([
  z.literal("idle"),
  z.literal("searching"),
  z.literal("has-results"),
  z.literal("no-results")
]);
var fn5 = z.custom((v) => typeof v === "function");
var UseSearchStateSchema = z.object({
  query: z.string(),
  rawQuery: z.string(),
  setQuery: fn5,
  clear: fn5,
  hits: z.array(SearchHitSchema),
  activeIndex: z.number().int(),
  setActiveIndex: fn5,
  moveActive: fn5,
  status: SearchStatusSchema,
  index: SearchIndexSchema
});

// src/validate-input.ts
function emit(fn6, parsed) {
  reportInvalidInput({
    lib: WEB_SEARCH_LIB,
    libToken: WEB_SEARCH_TOKEN,
    fn: fn6,
    parseResult: toZodLikeSafeParseError(parsed)
  });
}
function validateSearchProps(props) {
  const parsed = SearchPropsSchema.safeParse(props);
  if (!parsed.success) emit("Search", parsed);
}
function validateSearchInputProps(props) {
  const parsed = SearchInputPropsSchema.safeParse(props);
  if (!parsed.success) emit("SearchInput", parsed);
}
function validateSearchResultsProps(props) {
  const parsed = SearchResultsPropsSchema.safeParse(props);
  if (!parsed.success) emit("SearchResults", parsed);
}
function validateSearchResultItemProps(props) {
  const parsed = SearchResultItemPropsSchema.safeParse(props);
  if (!parsed.success) emit("SearchResultItem", parsed);
}
function validateUseSearchOptions(options) {
  const parsed = UseSearchOptionsSchema.safeParse(options);
  if (!parsed.success) emit("useSearch", parsed);
}
function validateSearchHitProps(props) {
  const parsed = SearchHitComponentPropsSchema.safeParse(props);
  if (!parsed.success) emit("SearchHit", parsed);
}
function validateHighlightSegmentsInput(input) {
  const parsed = highlightSegmentsInputSchema.safeParse(input);
  if (!parsed.success) emit("highlightSegments", parsed);
}
function validateHighlightSegmentsForTermsInput(input) {
  const parsed = highlightSegmentsForTermsInputSchema.safeParse(input);
  if (!parsed.success) emit("highlightSegmentsForTerms", parsed);
}
function validateIndexManifestsInput(input) {
  const parsed = indexManifestsInputSchema.safeParse(input);
  if (!parsed.success) emit("indexManifests", parsed);
}
function validateSearchManifestsInput(input) {
  const parsed = searchManifestsInputSchema.safeParse(input);
  if (!parsed.success) emit("searchManifests", parsed);
}

// src/version.ts
var LIB_VERSION = "0.3.0";
var SearchInput = forwardRef(function SearchInput2(props, ref) {
  validateSearchInputProps(props);
  const {
    value,
    onChange,
    placeholder = "Search\u2026",
    ariaLabel = "Search",
    listboxId,
    activeId = null,
    onKeyDown,
    onFocus,
    onBlur,
    autoFocus,
    className,
    testId = "search__input"
  } = props;
  const expanded = activeId !== null;
  const rootClass = ["search__input", className ?? ""].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx(
    "input",
    {
      ref,
      type: "search",
      role: "combobox",
      className: rootClass,
      value,
      onChange: (e) => onChange(e.target.value),
      onKeyDown,
      onFocus,
      onBlur,
      placeholder,
      "aria-label": ariaLabel,
      "aria-autocomplete": "list",
      "aria-controls": expanded ? listboxId : void 0,
      "aria-expanded": expanded,
      "aria-activedescendant": activeId ?? void 0,
      autoComplete: "off",
      autoCorrect: "off",
      autoCapitalize: "off",
      spellCheck: false,
      autoFocus,
      "data-testid": testId,
      ...withConformance("@norkron/web-search", LIB_VERSION)
    }
  );
});

// src/tokenize.ts
function tokenize(input) {
  if (typeof input !== "string" || input.length === 0) return [];
  const raw = input.toLowerCase().split(/\s+/);
  const out = [];
  for (const token of raw) {
    const stripped = stripPunct(token);
    if (stripped.length > 0) out.push(stripped);
  }
  return out;
}
var PUNCT_RE = /[\p{P}\p{S}]/u;
function stripPunct(token) {
  let start = 0;
  let end = token.length;
  while (start < end && PUNCT_RE.test(token.charAt(start))) start++;
  while (end > start && PUNCT_RE.test(token.charAt(end - 1))) end--;
  return token.slice(start, end);
}

// src/highlight-segments.ts
function segment(text, terms, caseSensitive) {
  if (terms.length === 0) return [{ text, match: false }];
  const haystack = caseSensitive ? text : text.toLowerCase();
  const ranges = [];
  for (const rawTerm of terms) {
    const term = caseSensitive ? rawTerm : rawTerm.toLowerCase();
    if (term.length === 0) continue;
    let pos = 0;
    while (pos < haystack.length) {
      const found = haystack.indexOf(term, pos);
      if (found === -1) break;
      ranges.push({ start: found, end: found + term.length });
      pos = found + term.length;
    }
  }
  if (ranges.length === 0) return [{ text, match: false }];
  ranges.sort((a, b) => a.start - b.start);
  const merged = [];
  for (const r of ranges) {
    const last = merged[merged.length - 1];
    if (last && r.start <= last.end) {
      if (r.end > last.end) last.end = r.end;
    } else {
      merged.push({ ...r });
    }
  }
  const out = [];
  let prev = 0;
  for (const r of merged) {
    if (r.start > prev) out.push({ text: text.slice(prev, r.start), match: false });
    out.push({ text: text.slice(r.start, r.end), match: true });
    prev = r.end;
  }
  if (prev < text.length) out.push({ text: text.slice(prev), match: false });
  return out;
}
function highlightSegments(text, query, options = {}) {
  validateHighlightSegmentsInput({ text, query, options });
  return segment(text, tokenize(query), options?.caseSensitive ?? false);
}
function highlightSegmentsForTerms(text, terms, options = {}) {
  validateHighlightSegmentsForTermsInput({ text, terms, options });
  return segment(text, terms ?? [], options?.caseSensitive ?? false);
}
function SearchResultItem(props) {
  validateSearchResultItemProps(props);
  const { hit, active, id, onSelect, onMouseEnter, testId = "search__result" } = props;
  const className = ["search__result", active ? "search__result--active" : ""].filter(Boolean).join(" ");
  const projectMeta = typeof hit.document.meta?.project === "string" ? hit.document.meta.project : null;
  const titleSegments = highlightSegmentsForTerms(hit.document.title, hit.matchedTerms);
  return /* @__PURE__ */ jsxs(
    "li",
    {
      id,
      role: "option",
      "aria-selected": active,
      className,
      onMouseEnter,
      onMouseDown: (e) => {
        e.preventDefault();
        onSelect();
      },
      "data-testid": testId,
      children: [
        /* @__PURE__ */ jsx(
          RichTooltip,
          {
            content: `Score: ${hit.score.toFixed(3)}`,
            trigger: /* @__PURE__ */ jsxs("span", { className: "search__result-title", children: [
              titleSegments.map(
                (seg, i) => seg.match ? /* @__PURE__ */ jsx("mark", { className: "search__highlight", children: seg.text }, i) : /* @__PURE__ */ jsx(Fragment, { children: seg.text }, i)
              ),
              projectMeta ? /* @__PURE__ */ jsx("span", { className: "search__result-meta", children: projectMeta }) : null
            ] })
          }
        ),
        hit.snippets.length > 0 ? /* @__PURE__ */ jsx("span", { className: "search__result-snippet", children: hit.snippets.flatMap(
          (snippet, si) => snippet.segments.map(
            (seg, gi) => seg.match ? /* @__PURE__ */ jsx("mark", { className: "search__highlight", children: seg.text }, `${si}-${gi}`) : /* @__PURE__ */ jsx(Fragment, { children: seg.text }, `${si}-${gi}`)
          )
        ) }) : null
      ]
    }
  );
}
function renderRow({
  hit,
  index,
  listboxId,
  activeIndex,
  query,
  onSelect,
  onHover,
  renderItem
}) {
  const id = `${listboxId}-row-${index}`;
  const active = index === activeIndex;
  if (renderItem) {
    return /* @__PURE__ */ jsx(
      "li",
      {
        id,
        role: "option",
        "aria-selected": active,
        className: ["search__result", active ? "search__result--active" : ""].filter(Boolean).join(" "),
        onMouseEnter: () => onHover?.(index),
        onMouseDown: (e) => {
          e.preventDefault();
          onSelect(hit, index);
        },
        children: renderItem(hit, { active, query })
      },
      hit.document.id
    );
  }
  return /* @__PURE__ */ jsx(
    SearchResultItem,
    {
      hit,
      query,
      active,
      id,
      onSelect: () => onSelect(hit, index),
      onMouseEnter: () => onHover?.(index)
    },
    hit.document.id
  );
}
function SearchResults(props) {
  validateSearchResultsProps(props);
  const {
    hits,
    query,
    activeIndex,
    listboxId,
    onSelect,
    onHover,
    renderItem,
    loading,
    error,
    onRetry,
    testId = "search__results",
    virtualize
  } = props;
  if (error) {
    const message = typeof error === "string" ? error : error.message;
    return /* @__PURE__ */ jsx(
      "div",
      {
        className: "search__results search__results--error",
        "data-testid": `${testId}__error`,
        role: "presentation",
        children: /* @__PURE__ */ jsx(ErrorState, { title: "Search failed", description: message, onRetry })
      }
    );
  }
  if (loading) {
    return /* @__PURE__ */ jsx(
      "div",
      {
        className: "search__results search__results--loading",
        "data-testid": `${testId}__loading`,
        role: "presentation",
        children: /* @__PURE__ */ jsx(LoadingState, { lines: 3, label: "Searching" })
      }
    );
  }
  if (query.length === 0) return null;
  if (hits.length === 0) {
    return /* @__PURE__ */ jsx(
      "ul",
      {
        id: listboxId,
        role: "listbox",
        className: "search__results search__results--empty",
        "data-testid": testId,
        "aria-label": "Search results",
        children: /* @__PURE__ */ jsx("li", { className: "search__empty", role: "option", "aria-selected": false, children: /* @__PURE__ */ jsx(EmptyState, { title: "No results", description: `No documents match "${query}"` }) })
      }
    );
  }
  if (virtualize) {
    return /* @__PURE__ */ jsx(
      VirtualizedResults,
      {
        hits,
        query,
        activeIndex,
        listboxId,
        onSelect,
        onHover,
        renderItem,
        testId,
        itemHeight: virtualize.itemHeight,
        maxHeight: virtualize.maxHeight,
        overscan: virtualize.overscan ?? 5
      }
    );
  }
  return /* @__PURE__ */ jsx(
    "ul",
    {
      id: listboxId,
      role: "listbox",
      className: "search__results",
      "data-testid": testId,
      "aria-label": "Search results",
      children: hits.map(
        (hit, index) => renderRow({
          hit,
          index,
          listboxId,
          activeIndex,
          query,
          onSelect,
          onHover,
          renderItem
        })
      )
    }
  );
}
function VirtualizedResults({
  hits,
  query,
  activeIndex,
  listboxId,
  onSelect,
  onHover,
  renderItem,
  testId,
  itemHeight,
  maxHeight,
  overscan
}) {
  const scrollRef = useRef(null);
  const { virtualItems, totalSize, scrollTo } = useVirtual({
    items: hits,
    estimateSize: itemHeight,
    scrollRef,
    overscan
  });
  if (activeIndex >= 0 && activeIndex < hits.length) {
    queueMicrotask(() => scrollTo(activeIndex));
  }
  return /* @__PURE__ */ jsx(
    "div",
    {
      ref: scrollRef,
      className: "search__results search__results--virtual virtual-list virtual-list--vertical",
      "data-testid": `${testId}__scroll`,
      style: { maxHeight, overflowY: "auto", position: "relative" },
      children: /* @__PURE__ */ jsx(
        "ul",
        {
          id: listboxId,
          role: "listbox",
          "aria-label": "Search results",
          className: "virtual-list__inner",
          "data-testid": testId,
          style: { position: "relative", height: totalSize, margin: 0, padding: 0 },
          children: virtualItems.map((v) => /* @__PURE__ */ jsx(
            "div",
            {
              role: "presentation",
              style: {
                position: "absolute",
                top: v.start,
                left: 0,
                right: 0,
                height: v.size
              },
              className: "virtual-list__row virtual-list__row--vertical",
              children: renderRow({
                hit: v.item,
                index: v.index,
                listboxId,
                activeIndex,
                query,
                onSelect,
                onHover,
                renderItem
              })
            },
            hits[v.index]?.document.id ?? v.index
          ))
        }
      )
    }
  );
}
function SearchFilters(props) {
  const { filters, className, testId = "search__filters" } = props;
  if (filters.length === 0) return null;
  const cls = ["search__filters", className ?? ""].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx("div", { className: cls, role: "list", "aria-label": "Active filters", "data-testid": testId, children: filters.map((f) => /* @__PURE__ */ jsx(SearchFilterChip, { filter: f }, f.id)) });
}
function SearchFilterChip({ filter }) {
  const handleKey = (e) => {
    if (e.key === "Backspace" || e.key === "Delete") {
      e.preventDefault();
      filter.onRemove();
    }
  };
  return /* @__PURE__ */ jsxs(
    "span",
    {
      className: "search__filter-chip",
      role: "listitem",
      "data-filter-id": filter.id,
      "data-testid": `search__filter-chip-${filter.id}`,
      tabIndex: 0,
      onKeyDown: handleKey,
      children: [
        /* @__PURE__ */ jsx("span", { className: "search__filter-chip-label", children: filter.label }),
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            className: "search__filter-chip-remove",
            "aria-label": `Remove filter ${filter.label}`,
            onClick: (e) => {
              e.preventDefault();
              filter.onRemove();
            },
            "data-testid": `search__filter-chip-${filter.id}__remove`,
            children: "\xD7"
          }
        )
      ]
    }
  );
}
function SearchFacets(props) {
  const { facets, selected, onToggle, className, testId = "search__facets" } = props;
  if (facets.length === 0) return null;
  const cls = ["search__facets", className ?? ""].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx("aside", { className: cls, "aria-label": "Facet filters", "data-testid": testId, children: facets.map((facet) => {
    const sel = selected[facet.id] ?? [];
    return /* @__PURE__ */ jsxs(
      "details",
      {
        className: "search__facet",
        "data-facet-id": facet.id,
        "data-testid": `search__facet-${facet.id}`,
        open: !facet.collapsed,
        children: [
          /* @__PURE__ */ jsx("summary", { className: "search__facet-summary", children: facet.label }),
          /* @__PURE__ */ jsx("ul", { className: "search__facet-options", role: "list", children: facet.options.map((opt) => {
            const isSelected = sel.includes(opt.value);
            return /* @__PURE__ */ jsx("li", { className: "search__facet-option-row", children: /* @__PURE__ */ jsxs(
              "button",
              {
                type: "button",
                className: [
                  "search__facet-option",
                  isSelected ? "search__facet-option--selected" : ""
                ].filter(Boolean).join(" "),
                "aria-pressed": isSelected,
                onClick: (e) => {
                  e.preventDefault();
                  onToggle(facet.id, opt.value);
                },
                "data-testid": `search__facet-${facet.id}__option-${opt.value}`,
                children: [
                  /* @__PURE__ */ jsx("span", { className: "search__facet-option-label", children: opt.label }),
                  typeof opt.count === "number" ? /* @__PURE__ */ jsx("span", { className: "search__facet-option-count", children: opt.count }) : null
                ]
              }
            ) }, opt.value);
          }) })
        ]
      },
      facet.id
    );
  }) });
}
function SearchRecents(props) {
  const { recents, onSelect, onClear, listboxId, className, testId = "search__recents" } = props;
  if (recents.length === 0) return null;
  const cls = ["search__recents", className ?? ""].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsxs("div", { className: cls, "data-testid": testId, children: [
    /* @__PURE__ */ jsxs("div", { className: "search__recents-header", children: [
      /* @__PURE__ */ jsx("span", { className: "search__recents-label", children: "Recent" }),
      onClear ? /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          className: "search__recents-clear",
          onClick: (e) => {
            e.preventDefault();
            onClear();
          },
          "data-testid": "search__recents-clear",
          children: "Clear"
        }
      ) : null
    ] }),
    /* @__PURE__ */ jsx(
      "ul",
      {
        className: "search__recents-list",
        role: "listbox",
        "aria-label": "Recent searches",
        id: listboxId,
        children: recents.map((term) => /* @__PURE__ */ jsx("li", { role: "option", "aria-selected": false, className: "search__recents-item", children: /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            className: "search__recents-button",
            onMouseDown: (e) => {
              e.preventDefault();
              onSelect(term);
            },
            "data-testid": `search__recents-item-${term}`,
            children: term
          }
        ) }, term))
      }
    )
  ] });
}

// src/engine.ts
var DEFAULT_LIMIT = 20;
var DEFAULT_MAX_SNIPPETS = 2;
var DEFAULT_SNIPPET_LEN = 120;
function indexManifests(manifests) {
  validateIndexManifestsInput({ manifests });
  const documents = {};
  const invertedIndex = {};
  const documentLengths = {};
  for (const manifest of manifests) {
    for (const doc of manifest.documents) {
      if (doc.id in documents) continue;
      const stamped = manifest.project && !(doc.meta && "project" in doc.meta) ? { ...doc, meta: { ...doc.meta ?? {}, project: manifest.project } } : doc;
      documents[doc.id] = stamped;
      const titleTokens = tokenize(doc.title);
      const bodyTokens = doc.body ? tokenize(doc.body) : [];
      const tokens = [...titleTokens, ...titleTokens, ...bodyTokens];
      documentLengths[doc.id] = tokens.length;
      for (let position = 0; position < tokens.length; position++) {
        const term = tokens[position];
        if (!term) continue;
        const postings = invertedIndex[term];
        if (postings && postings.length > 0) {
          const last = postings[postings.length - 1];
          if (last && last.documentId === doc.id) {
            last.positions.push(position);
          } else {
            postings.push({ documentId: doc.id, positions: [position] });
          }
        } else {
          invertedIndex[term] = [{ documentId: doc.id, positions: [position] }];
        }
      }
    }
  }
  const terms = Object.keys(invertedIndex).sort();
  return { documents, invertedIndex, documentLengths, terms };
}
function searchManifests(index, query, options = {}) {
  validateSearchManifestsInput({ index, query, options });
  const limit = options.limit ?? DEFAULT_LIMIT;
  const fuzzy = options.fuzzy ?? true;
  const maxSnippets = options.maxSnippets ?? DEFAULT_MAX_SNIPPETS;
  const snippetLength = options.snippetLength ?? DEFAULT_SNIPPET_LEN;
  const queryTokens = tokenize(query);
  if (queryTokens.length === 0) return [];
  const documentIds = Object.keys(index.documents);
  const totalDocs = documentIds.length || 1;
  const scoreById = /* @__PURE__ */ new Map();
  const matchedTermsById = /* @__PURE__ */ new Map();
  for (const qToken of queryTokens) {
    const exactPostings = index.invertedIndex[qToken];
    if (exactPostings && exactPostings.length > 0) {
      const idf = Math.log(1 + totalDocs / exactPostings.length);
      for (const p of exactPostings) {
        const tf = p.positions.length;
        const dl = index.documentLengths[p.documentId] || 1;
        const tfNorm = (1 + Math.log(tf)) / Math.log(1 + dl);
        const inc = tfNorm * idf;
        scoreById.set(p.documentId, (scoreById.get(p.documentId) ?? 0) + inc);
        const matched = matchedTermsById.get(p.documentId) ?? /* @__PURE__ */ new Set();
        matched.add(qToken);
        matchedTermsById.set(p.documentId, matched);
      }
    }
    if (fuzzy && qToken.length >= 3) {
      for (const term of index.terms) {
        if (term === qToken) continue;
        if (Math.abs(term.length - qToken.length) > 1) continue;
        if (!withinEditDistance1(qToken, term)) continue;
        const postings = index.invertedIndex[term];
        if (!postings || postings.length === 0) continue;
        const idf = Math.log(1 + totalDocs / postings.length);
        for (const p of postings) {
          const tf = p.positions.length;
          const dl = index.documentLengths[p.documentId] || 1;
          const tfNorm = (1 + Math.log(tf)) / Math.log(1 + dl);
          const inc = 0.5 * tfNorm * idf;
          scoreById.set(p.documentId, (scoreById.get(p.documentId) ?? 0) + inc);
          const matched = matchedTermsById.get(p.documentId) ?? /* @__PURE__ */ new Set();
          matched.add(qToken);
          matchedTermsById.set(p.documentId, matched);
        }
      }
    }
  }
  const ranked = [];
  for (const [id, score] of scoreById) {
    const doc = index.documents[id];
    if (!doc) continue;
    const matched = [...matchedTermsById.get(id) ?? /* @__PURE__ */ new Set()];
    ranked.push({
      document: doc,
      score,
      matchedTerms: matched,
      snippets: buildSnippets(doc, queryTokens, maxSnippets, snippetLength)
    });
  }
  ranked.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return a.document.id < b.document.id ? -1 : a.document.id > b.document.id ? 1 : 0;
  });
  return ranked.slice(0, limit);
}
function withinEditDistance1(a, b) {
  if (a === b) return true;
  const la = a.length;
  const lb = b.length;
  if (Math.abs(la - lb) > 1) return false;
  if (la === lb) {
    let diffs = 0;
    for (let i2 = 0; i2 < la; i2++) {
      if (a.charCodeAt(i2) !== b.charCodeAt(i2)) {
        diffs++;
        if (diffs > 1) return false;
      }
    }
    return diffs <= 1;
  }
  const longer = la > lb ? a : b;
  const shorter = la > lb ? b : a;
  let i = 0;
  let j = 0;
  let edits = 0;
  while (i < longer.length && j < shorter.length) {
    if (longer.charCodeAt(i) === shorter.charCodeAt(j)) {
      i++;
      j++;
    } else {
      edits++;
      if (edits > 1) return false;
      i++;
    }
  }
  if (i < longer.length) edits++;
  return edits <= 1;
}
function buildSnippets(doc, queryTokens, maxSnippets, snippetLength) {
  if (queryTokens.length === 0) return [];
  const lcQuery = queryTokens.map((t) => t.toLowerCase());
  const source = doc.body && doc.body.length > 0 ? doc.body : doc.title;
  if (!source) return [];
  const lcSource = source.toLowerCase();
  const matchRanges = [];
  for (const tok of lcQuery) {
    let pos = 0;
    while (pos < lcSource.length) {
      const found = lcSource.indexOf(tok, pos);
      if (found === -1) break;
      matchRanges.push({ start: found, end: found + tok.length });
      pos = found + tok.length;
    }
  }
  if (matchRanges.length === 0) {
    return [
      {
        segments: [{ text: source.slice(0, snippetLength), match: false }]
      }
    ];
  }
  matchRanges.sort((a, b) => a.start - b.start);
  const snippets = [];
  let cursor = 0;
  for (const range of matchRanges) {
    if (snippets.length >= maxSnippets) break;
    if (range.start < cursor) continue;
    const halfWindow = Math.floor(snippetLength / 2);
    const winStart = Math.max(0, range.start - halfWindow);
    const winEnd = Math.min(source.length, range.start + halfWindow);
    const inWindow = matchRanges.filter((r) => r.start >= winStart && r.end <= winEnd);
    const segments = [];
    let prev = winStart;
    for (const r of inWindow) {
      if (r.start > prev) segments.push({ text: source.slice(prev, r.start), match: false });
      segments.push({ text: source.slice(r.start, r.end), match: true });
      prev = r.end;
    }
    if (prev < winEnd) segments.push({ text: source.slice(prev, winEnd), match: false });
    if (winStart > 0) segments.unshift({ text: "\u2026", match: false });
    if (winEnd < source.length) segments.push({ text: "\u2026", match: false });
    snippets.push({ segments });
    cursor = winEnd;
  }
  return snippets;
}

// src/use-search.ts
var EMPTY_MANIFESTS = Object.freeze([]);
var EMPTY_INDEX = Object.freeze({
  documents: Object.freeze({}),
  invertedIndex: Object.freeze({}),
  documentLengths: Object.freeze({}),
  terms: Object.freeze([])
});
function useSearch(options = {}) {
  validateUseSearchOptions(options);
  const {
    manifests = EMPTY_MANIFESTS,
    index: providedIndex,
    debounceMs = 100,
    limit = 20,
    fuzzy = true,
    initialQuery = ""
  } = options;
  const [rawQuery, setRawQuery] = useState(initialQuery);
  const [query, setDebouncedQuery] = useState(initialQuery);
  const [activeIndex, setActiveIndex] = useState(-1);
  const index = useMemo(() => {
    if (providedIndex) return providedIndex;
    if (manifests.length === 0) return EMPTY_INDEX;
    return indexManifests(manifests);
  }, [providedIndex, manifests]);
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (debounceMs <= 0) {
      setDebouncedQuery(rawQuery);
      return;
    }
    debounceRef.current = setTimeout(() => setDebouncedQuery(rawQuery), debounceMs);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [rawQuery, debounceMs]);
  const hits = useMemo(() => {
    const trimmed = query.trim();
    if (trimmed.length === 0) return [];
    return searchManifests(index, trimmed, { limit, fuzzy });
  }, [index, query, limit, fuzzy]);
  useEffect(() => {
    if (hits.length === 0) {
      setActiveIndex(-1);
    } else if (activeIndex >= hits.length) {
      setActiveIndex(0);
    } else if (activeIndex < 0) {
      setActiveIndex(0);
    }
  }, [hits, activeIndex]);
  const status = useMemo(() => {
    if (query.trim().length === 0) {
      return rawQuery.trim().length === 0 ? "idle" : "searching";
    }
    if (rawQuery !== query) return "searching";
    return hits.length > 0 ? "has-results" : "no-results";
  }, [rawQuery, query, hits.length]);
  const setQuery = useCallback((next) => {
    setRawQuery(next);
  }, []);
  const clear = useCallback(() => {
    setRawQuery("");
    setDebouncedQuery("");
    setActiveIndex(-1);
  }, []);
  const moveActive = useCallback(
    (delta) => {
      setActiveIndex((current) => {
        if (hits.length === 0) return -1;
        const base = current < 0 ? delta > 0 ? -1 : hits.length : current;
        const next = base + delta;
        if (next < 0) return hits.length - 1;
        if (next >= hits.length) return 0;
        return next;
      });
    },
    [hits.length]
  );
  return {
    query,
    rawQuery,
    setQuery,
    clear,
    hits,
    activeIndex,
    setActiveIndex,
    moveActive,
    status,
    index
  };
}
function useRecents(storageKey, capacity = 8) {
  const handle = useMemo(() => {
    if (!storageKey) return null;
    try {
      return mru(storageKey, { capacity });
    } catch {
      return null;
    }
  }, [storageKey, capacity]);
  const [recents, setRecents] = useState(() => {
    if (!handle) return [];
    try {
      return handle.list();
    } catch {
      return [];
    }
  });
  const push = useCallback(
    (term) => {
      const trimmed = term.trim();
      if (!handle || trimmed.length === 0) return;
      try {
        handle.push(trimmed);
        setRecents(handle.list());
      } catch {
      }
    },
    [handle]
  );
  const clear = useCallback(() => {
    if (!handle) return;
    try {
      handle.clear();
      setRecents([]);
    } catch {
    }
  }, [handle]);
  return { recents, push, clear };
}
function Search(props) {
  validateSearchProps(props);
  const {
    manifests,
    index: providedIndex,
    placeholder,
    ariaLabel,
    debounceMs,
    limit,
    fuzzy,
    onSelect,
    initialQuery,
    renderItem,
    filters,
    facets,
    selectedFacets: selectedFacetsProp,
    onFacetToggle,
    recentsKey,
    recentsLimit = 8,
    loading,
    error,
    onRetry,
    className,
    style,
    testId = "search"
  } = props;
  const reactId = useId();
  const listboxId = `${reactId}-listbox`;
  const inputRef = useRef(null);
  useFocusRestore(true);
  const accent = useThemeVar("color-fg-accent");
  const state = useSearch({
    manifests,
    index: providedIndex,
    debounceMs,
    limit,
    fuzzy,
    initialQuery
  });
  const recentsApi = useRecents(recentsKey, recentsLimit);
  const [isFocused, setFocused] = useState(false);
  const [internalSelected, setInternalSelected] = useState({});
  const selectedFacets = selectedFacetsProp ?? internalSelected;
  const handleFacetToggle = useCallback(
    (facetId, value) => {
      onFacetToggle?.(facetId, value);
      if (selectedFacetsProp === void 0) {
        setInternalSelected((prev) => {
          const current = prev[facetId] ?? [];
          const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
          return { ...prev, [facetId]: next };
        });
      }
    },
    [onFacetToggle, selectedFacetsProp]
  );
  const handleSelect = useCallback(
    (hit) => {
      onSelect?.(hit);
      if (recentsKey && state.query.trim().length > 0) {
        recentsApi.push(state.query.trim());
      }
      state.clear();
    },
    [onSelect, state, recentsKey, recentsApi]
  );
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        state.moveActive(1);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        state.moveActive(-1);
      } else if (e.key === "Home") {
        if (state.hits.length > 0) {
          e.preventDefault();
          state.setActiveIndex(0);
        }
      } else if (e.key === "End") {
        if (state.hits.length > 0) {
          e.preventDefault();
          state.setActiveIndex(state.hits.length - 1);
        }
      } else if (e.key === "Enter") {
        if (state.activeIndex >= 0 && state.activeIndex < state.hits.length) {
          e.preventDefault();
          const hit = state.hits[state.activeIndex];
          if (hit) handleSelect(hit);
        } else if (recentsKey && state.rawQuery.trim().length > 0) {
          recentsApi.push(state.rawQuery.trim());
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        if (state.rawQuery.length > 0) {
          state.clear();
        } else {
          inputRef.current?.blur();
        }
      }
    },
    [state, handleSelect, recentsKey, recentsApi]
  );
  const activeId = state.activeIndex >= 0 && state.activeIndex < state.hits.length ? `${listboxId}-row-${state.activeIndex}` : null;
  const rootStyle = useMemo(
    () => ({
      ...accent ? { ["--search__accent"]: accent } : {},
      ...style
    }),
    [accent, style]
  );
  const rootClass = ["search", className ?? ""].filter(Boolean).join(" ");
  const showRecents = !!recentsKey && isFocused && state.rawQuery.length === 0 && recentsApi.recents.length > 0 && !loading && !error;
  const hasFacets = (facets?.length ?? 0) > 0;
  useEffect(() => {
    if (selectedFacetsProp !== void 0) setInternalSelected({});
  }, [selectedFacetsProp]);
  const handleRecentSelect = useCallback(
    (term) => {
      state.setQuery(term);
      inputRef.current?.focus();
    },
    [state]
  );
  return /* @__PURE__ */ jsx(ErrorBoundary, { fallback: (err, reset) => /* @__PURE__ */ jsx(DefaultErrorFallback, { error: err, reset }), children: /* @__PURE__ */ jsxs(
    "div",
    {
      ...withConformance("@norkron/web-search", LIB_VERSION),
      className: rootClass,
      style: rootStyle,
      "data-testid": testId,
      children: [
        /* @__PURE__ */ jsx(
          SearchInput,
          {
            ref: inputRef,
            value: state.rawQuery,
            onChange: state.setQuery,
            placeholder,
            ariaLabel,
            listboxId,
            activeId,
            onKeyDown: handleKeyDown,
            onFocus: () => setFocused(true),
            onBlur: () => setFocused(false),
            testId: `${testId}__input`
          }
        ),
        filters && filters.length > 0 ? /* @__PURE__ */ jsx(SearchFilters, { filters, testId: `${testId}__filters` }) : null,
        showRecents ? /* @__PURE__ */ jsx(
          SearchRecents,
          {
            recents: recentsApi.recents,
            onSelect: handleRecentSelect,
            onClear: recentsApi.clear,
            testId: `${testId}__recents`
          }
        ) : null,
        /* @__PURE__ */ jsxs("div", { className: ["search__body", hasFacets ? "search__body--with-facets" : ""].filter(Boolean).join(" "), children: [
          hasFacets ? /* @__PURE__ */ jsx(
            SearchFacets,
            {
              facets: facets ?? [],
              selected: selectedFacets,
              onToggle: handleFacetToggle,
              testId: `${testId}__facets`
            }
          ) : null,
          /* @__PURE__ */ jsx(
            SearchResults,
            {
              hits: state.hits,
              query: state.query,
              activeIndex: state.activeIndex,
              listboxId,
              onSelect: (hit) => handleSelect(hit),
              onHover: (i) => state.setActiveIndex(i),
              renderItem,
              loading,
              error: error ?? null,
              onRetry,
              testId: `${testId}__results`
            }
          )
        ] })
      ]
    }
  ) });
}
function SearchHit(props) {
  validateSearchHitProps(props);
  const { text, query, className, caseSensitive = false } = props;
  const cls = ["search__hit", className ?? ""].filter(Boolean).join(" ");
  const segments = highlightSegments(text, query, { caseSensitive });
  return /* @__PURE__ */ jsx("span", { className: cls, children: segments.map(
    (seg, i) => seg.match ? /* @__PURE__ */ jsx("mark", { className: "search__highlight", children: seg.text }, i) : /* @__PURE__ */ jsx(Fragment, { children: seg.text }, i)
  ) });
}
var SelfCheckOptionsSchema = z.object({
  budgetMs: z.number().int().positive().max(5e3).optional(),
  minSeverity: z.enum(["critical", "major", "minor", "info"]).optional(),
  locale: z.string().min(2).max(35).optional()
}).strict();

// src/validate-self-check.ts
var DEFAULT_BUDGET_MS = 50;
function parseSelfCheckOptions(opts) {
  const parsed = SelfCheckOptionsSchema.safeParse(opts ?? {});
  if (parsed.success) {
    return {
      budgetMs: parsed.data.budgetMs ?? DEFAULT_BUDGET_MS,
      minSeverity: parsed.data.minSeverity ?? "info"
    };
  }
  return { budgetMs: DEFAULT_BUDGET_MS, minSeverity: "info" };
}

// src/self-check.ts
var LIB_ID = "@norkron/web-search";
var SEVERITY_RANK = {
  info: 0,
  minor: 1,
  major: 2,
  critical: 3
};
function meetsFloor(finding, floor) {
  return SEVERITY_RANK[finding.severity] >= SEVERITY_RANK[floor];
}
function selectorFor(el) {
  const testid = el.getAttribute?.("data-testid");
  if (testid) return `[data-testid="${testid}"]`;
  const tag = el.tagName.toLowerCase();
  const cls = el.getAttribute?.("class")?.split(/\s+/).filter(Boolean).slice(0, 2).join(".");
  return cls ? `${tag}.${cls}` : tag;
}
function selfCheck(root, opts) {
  const safeOpts = parseSelfCheckOptions(opts);
  const findings = [];
  try {
    const qsa = root.querySelectorAll;
    if (typeof qsa !== "function") return findings;
    const instances = Array.from(
      qsa.call(root, '[data-norkron-lib^="@norkron/web-search"]')
    );
    for (const instance of instances) {
      const input = instance.querySelector?.('input[role="combobox"], input.search__input');
      const empty = !input?.getAttribute?.("value");
      const emptyPanel = instance.querySelector?.(
        '.search__no-results, [data-testid$="search-results__empty"], [data-testid$="__results__empty"]'
      );
      if (empty && emptyPanel) {
        findings.push({
          libId: LIB_ID,
          code: "SCH_EMPTY_SEARCH_NO_RESULTS",
          category: "flow",
          severity: "minor",
          message: 'Search input is empty but a "No results" panel is rendered. Likely a UX dead end on initial mount \u2014 render only after the first query.',
          selector: selectorFor(emptyPanel),
          suggestedFix: "Gate the no-results panel on `query.length > 0` (or show recent searches / suggestions when empty)."
        });
      }
    }
  } catch (err) {
    findings.push({
      libId: LIB_ID,
      code: "SCH_SELFCHECK_INTERNAL_ERROR",
      category: "identity",
      severity: "info",
      message: `selfCheck threw internally: ${err instanceof Error ? err.message : String(err)}`
    });
  }
  return findings.filter((f) => meetsFloor(f, safeOpts.minSeverity));
}
var RESPONSIVE_CONTRACT = {
  lib: "@norkron/web-search",
  supportedBreakpoints: ["xs", "sm", "md", "lg", "xl", "2xl", "3xl"],
  breakpointBehavior: {
    // Grounded in search.css (no @media / @container, fluid width: 100%)
    // + Search.tsx / SearchInput.tsx / SearchResults.tsx /
    // SearchResultItem.tsx (no viewport-keyed branching). Honest
    // baseline: 'full' at every tier.
    xs: "full",
    sm: "full",
    md: "full",
    lg: "full",
    xl: "full",
    "2xl": "full",
    "3xl": "full"
  },
  containerQueries: [],
  rwdSelfCheckCodes: []
};
registerResponsiveContract(RESPONSIVE_CONTRACT.lib, RESPONSIVE_CONTRACT);

// src/index.ts
registerLazy(
  "@norkron/web-search",
  () => selfCheck
);

export { DocumentSchema, HighlightSegmentsOptionsSchema, LIB_VERSION, ManifestSchema, PostingSchema, RESPONSIVE_CONTRACT, Search, SearchFacets, SearchFilters, SearchHit, SearchHitSchema, SearchIndexSchema, SearchInput, SearchInputPropsSchema, SearchInputSchema, SearchManifestsOptionsSchema, SearchPropsSchema, SearchRecents, SearchResultItem, SearchResultItemPropsSchema, SearchResultItemSchema, SearchResults, SearchResultsPropsSchema, SearchResultsSchema, SearchSchema, SearchSegmentSchema, SearchSnippetSchema, SearchStatusSchema, UseSearchOptionsSchema, UseSearchStateSchema, buildSnippets, highlightSegments, highlightSegmentsForTerms, highlightSegmentsForTermsInputSchema, highlightSegmentsForTermsSchema, highlightSegmentsInputSchema, highlightSegmentsSchema, indexManifests, indexManifestsInputSchema, indexManifestsSchema, searchManifests, searchManifestsInputSchema, searchManifestsSchema, selfCheck, stripPunct, tokenize, useRecents, useSearch, withinEditDistance1 };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map